import {
  Box,
  Divider,
  FlexBox,
  Icon,
  InputRichText,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { useContext, useEffect, useMemo } from 'react';

import {
  findLabelByLanguageCode,
  LanguageCode,
} from 'app/utils/supportedLanguages';
import { AiLoadingState } from 'components/aiLoadingState';
import { useEditorContext } from 'components/editorCopilotDropdown/utils';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useTranslateText } from 'utils/useTranslateText';

interface QuickTranslateProps {
  language: string;
  text: string | 'html';
}

export const QuickTranslate = ({
  language,
  text,
}: QuickTranslateProps): JSX.Element | null => {
  const [translate, { asyncStatus, translatedText }] = useTranslateText();
  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();
  const { hasCopilot } = useCopilotTrial();

  const isDisabled = useMemo(() => {
    return !language;
  }, [language]);

  useEffect(() => {
    if (asyncStatus === 'uninitialized') {
      content.setUiBlocker(null);
    } else if (asyncStatus === 'pending') {
      content.setUiBlocker(
        <Box style={{ position: 'absolute', bottom: 16 }}>
          <AiLoadingState
            label={`Translating to ${findLabelByLanguageCode(
              language as LanguageCode,
            )}`}
          />
        </Box>,
      );
    } else if (asyncStatus === 'rejected') {
      content.setUiBlocker(
        <FlexBox
          style={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            paddingBottom: '30px',
          }}
          alignItems="flex-end"
          justifyContent="center"
          gap="200"
          onClick={() => {
            content.setUiBlocker(null);
          }}
        >
          <Text.Paragraph
            variant="1"
            color={typography.color.error}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Icon
              name="exclamation"
              color={typography.color.error}
              style={{ marginRight: '8px' }}
            />
            Failed to translate
          </Text.Paragraph>
        </FlexBox>,
      );
    } else if (asyncStatus === 'fulfilled') {
      replace(translatedText ?? '');
      content.setUiBlocker(null);
    }
  }, [asyncStatus, content, language, replace, translatedText]);

  if (!hasCopilot) {
    return null;
  }

  return (
    <>
      <Divider orientation="vertical" />
      <Tooltip
        content={
          isDisabled
            ? "Can't find the conversation's language"
            : "Translate to conversation's language"
        }
        dark
      >
        <InputRichText.ToolbarButton
          icon="translate"
          text="Quick translate"
          onClick={() => {
            translate({ language, text });
          }}
          disabled={isDisabled}
        />
      </Tooltip>
    </>
  );
};
